/* =========================================================================
   SightForest — global.js  (vanilla, no dependencies)
   ========================================================================= */
(function () {
  'use strict';

  var SF = window.SightForest || {};
  var routes = SF.routes || {};

  function money(cents) {
    // Lightweight money formatter using shop money_format. Falls back gracefully.
    try {
      var amount = (cents / 100).toFixed(2);
      var fmt = SF.moneyFormat || '${{amount}}';
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    } catch (e) { return (cents / 100).toFixed(2); }
  }

  function embedVideo(url) {
    if (!url) return '';
    var yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    if (yt) return '<iframe src="https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0&playsinline=1" title="Video" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
    var vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return '<iframe src="https://player.vimeo.com/video/' + vm[1] + '?autoplay=1" title="Video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    return '<video src="' + url.replace(/"/g, '') + '" autoplay controls playsinline></video>';
  }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  // Build cart items for a PDP add/buy button: pack math + paid add-ons + free gift.
  function buildBundleItems(btn) {
    var form = btn.closest('[data-product-form]');
    var qi = form ? form.querySelector('[data-qty-input]') : null;
    var packSize = parseInt(btn.getAttribute('data-pack-size'), 10) || 1;
    var units = qi ? ((parseInt(qi.value, 10) || 1) * packSize) : (parseInt(btn.getAttribute('data-quantity'), 10) || 1);
    var items = [{ id: parseInt(btn.getAttribute('data-variant-id'), 10), quantity: units }];
    var giftVar = form && form.getAttribute('data-gift-variant');
    var giftMin = form ? (parseInt(form.getAttribute('data-gift-min'), 10) || 0) : 0;
    var qualifies = !!giftVar && giftMin > 0 && units >= giftMin;
    if (form) {
      Array.prototype.slice.call(form.querySelectorAll('[data-addon]:checked')).forEach(function (cb) {
        var vid = cb.getAttribute('data-variant-id');
        if (qualifies && vid === giftVar) return;
        items.push({ id: parseInt(vid, 10), quantity: 1 });
      });
    }
    if (qualifies) items.push({ id: parseInt(giftVar, 10), quantity: units, properties: { _gift: 'true' } });
    return items;
  }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function fetchJSON(url, opts) {
    return fetch(url, opts).then(function (r) { return r.json(); });
  }

  /* Serialize every cart write. Shopify's cart AJAX API is not concurrency-safe,
     so overlapping change/add/gift-sync calls can return an inconsistent cart.
     Each task runs only after the previous one settles. */
  var cartQueue = Promise.resolve();
  function enqueueCart(task) {
    var run = cartQueue.then(task, task);
    cartQueue = run.then(function () {}, function () {});
    return run;
  }
  // Per-line debounce so rapid +/- clicks collapse into a single request with the final qty.
  var lineTimers = {};
  function scheduleLineChange(key, qty) {
    if (lineTimers[key]) clearTimeout(lineTimers[key]);
    lineTimers[key] = setTimeout(function () { delete lineTimers[key]; Cart.changeLine(key, qty); }, 200);
  }

  /* ---------------------------------------------------------------------
     Cart store — fetch state, add/change lines, re-render via Section API
     --------------------------------------------------------------------- */
  var Cart = {
    sectionsToRender: function () {
      var ids = ['cart-drawer', 'cart-icon-bubble', 'main-cart', 'free-shipping-bar'];
      return ids.filter(function (id) { return document.getElementById('shopify-section-' + id) || document.querySelector('[data-cart-section="' + id + '"]'); });
    },

    add: function (items, opts) {
      opts = opts || {};
      var body = { items: items, sections: this.sectionRequestList(), sections_url: window.location.pathname };
      return enqueueCart(function () {
        return fetchJSON(routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (res) {
          if (res.status) { document.dispatchEvent(new CustomEvent('cart:error', { detail: res })); return res; }
          return Cart.refresh().then(function () {
            if (opts.openDrawer !== false && SF.cartType === 'drawer') Drawer.open();
            document.dispatchEvent(new CustomEvent('cart:added', { detail: res }));
            return res;
          });
        });
      });
    },

    // Line change + free-gift mirror in ONE request via /cart/update.js, so the
    // drawer repaints once (Cart.change alone triggers syncGifts → a second
    // request + repaint, which reads as a laggy double update).
    changeLine: function (key, quantity) {
      if (typeof key !== 'string') return Cart.change(key, quantity);
      var updates = {};
      updates[key] = quantity;
      var lineEl = document.querySelector('.cart-line[data-line-key="' + key + '"]');
      var giftBar = lineEl && lineEl.querySelector('.cart-gift-bar[data-line-key]');
      if (giftBar) {
        // One gift per driver unit (min 1) — syncGifts remains as a safety net.
        updates[giftBar.getAttribute('data-line-key')] = quantity;
      }
      return enqueueCart(function () {
        var body = { updates: updates, sections: Cart.sectionRequestList(), sections_url: window.location.pathname };
        return fetchJSON(routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (res) {
          Cart.applySections(res.sections);
          Cart.updateBubble(res);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: res }));
          return res;
        });
      });
    },

    // lineOrKey: a line key string (preferred — stable when gift lines shift) or a 1-based index.
    change: function (lineOrKey, quantity) {
      return enqueueCart(function () {
        var body = { quantity: quantity, sections: Cart.sectionRequestList(), sections_url: window.location.pathname };
        if (typeof lineOrKey === 'string') body.id = lineOrKey; else body.line = lineOrKey;
        return fetchJSON(routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (res) {
          Cart.applySections(res.sections);
          Cart.updateBubble(res);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: res }));
          return res;
        });
      });
    },

    sectionRequestList: function () {
      var list = [];
      $all('[data-cart-section]').forEach(function (el) {
        var id = el.getAttribute('data-cart-section');
        if (list.indexOf(id) === -1) list.push(id);
      });
      return list;
    },

    refresh: function () {
      return fetchJSON(routes.cart + '?sections=' + this.sectionRequestList().join(',') + '&t=' + Date.now())
        .then(function (sections) {
          Cart.applySections(sections);
          return fetchJSON(routes.cart + '.js');
        })
        .then(function (cart) { Cart.updateBubble(cart); return cart; });
    },

    applySections: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        $all('[data-cart-section="' + id + '"]').forEach(function (target) {
          var html = new DOMParser().parseFromString(sections[id], 'text/html');
          var fresh = html.querySelector('[data-cart-section="' + id + '"]') || html.body;
          target.innerHTML = fresh.innerHTML;
        });
      });
      bindAll(document);
    },

    updateBubble: function (cartOrCount) {
      // Accept a cart object (preferred — excludes free gift lines) or a raw number.
      var count = cartOrCount;
      if (cartOrCount && typeof cartOrCount === 'object' && cartOrCount.items) {
        count = cartOrCount.items.reduce(function (n, it) { return n + (isGiftLine(it) ? 0 : it.quantity); }, 0);
      }
      $all('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.setAttribute('data-count', count);
      });
    }
  };
  window.SFCart = Cart;

  /* ---------------------------------------------------------------------
     Free-gift auto-sync — keep the offered product in step with the cart.
     Rules (driver product id -> gift variant id + min qty) are emitted by
     the cart sections via snippets/gift-rules.liquid and refresh with them.
     The gift is auto-ADDED when a driver reaches its threshold and auto-
     REMOVED when it drops below — no matter where the qty changes.
     --------------------------------------------------------------------- */
  var giftSyncing = false;
  function isGiftLine(it) {
    return !!(it.properties && (it.properties._gift === 'true' || it.properties._gift === true));
  }
  function syncGifts() {
    if (giftSyncing) return;
    var el = document.querySelector('[data-gift-rules]');
    if (!el) return;
    var rules;
    try { rules = JSON.parse(el.textContent || '[]'); } catch (e) { return; }
    if (!rules.length) return;
    giftSyncing = true;
    fetchJSON(routes.cart + '.js?t=' + Date.now()).then(function (cart) {
      var driverQty = {};
      cart.items.forEach(function (it) {
        if (!isGiftLine(it)) driverQty[it.product_id] = (driverQty[it.product_id] || 0) + it.quantity;
      });
      // One free gift per qualifying driver unit (e.g. 2 cameras → 2 free SD cards).
      // desired[variantId] = target gift quantity.
      var desired = {};
      rules.forEach(function (r) {
        var dq = driverQty[r.productId] || 0;
        if (dq >= r.min) { var k = String(r.giftVariantId); desired[k] = (desired[k] || 0) + dq; }
      });
      // Reconcile every gift line to exactly the desired quantity: collapse
      // duplicate gift lines, correct wrong counts, remove stale gifts.
      var seen = {}, changeOps = [], addItems = [];
      cart.items.forEach(function (it) {
        if (!isGiftLine(it)) return;
        var vid = String(it.variant_id);
        var want = desired[vid] || 0;
        if (seen[vid]) {
          changeOps.push({ id: it.key, quantity: 0 }); // duplicate gift line → drop
        } else {
          seen[vid] = true;
          if (want === 0) changeOps.push({ id: it.key, quantity: 0 });
          else if (it.quantity !== want) changeOps.push({ id: it.key, quantity: want });
        }
      });
      Object.keys(desired).forEach(function (gv) {
        if (!seen[gv]) addItems.push({ id: parseInt(gv, 10), quantity: desired[gv], properties: { _gift: 'true' } });
      });
      if (!addItems.length && !changeOps.length) { giftSyncing = false; return; }
      var chain = Promise.resolve();
      if (addItems.length) {
        chain = chain.then(function () {
          return enqueueCart(function () {
            return fetch(routes.cartAdd, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: addItems }) });
          });
        });
      }
      changeOps.forEach(function (op) {
        chain = chain.then(function () {
          return enqueueCart(function () {
            return fetch(routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: op.id, quantity: op.quantity }) });
          });
        });
      });
      chain.then(function () { giftSyncing = false; Cart.refresh(); })
        .catch(function () { giftSyncing = false; });
    }).catch(function () { giftSyncing = false; });
  }
  window.SFSyncGifts = syncGifts;
  document.addEventListener('cart:added', syncGifts);
  document.addEventListener('cart:updated', syncGifts);

  /* ---------------------------------------------------------------------
     Cart drawer
     --------------------------------------------------------------------- */
  var Drawer = {
    open: function () { document.body.classList.add('cart-drawer-open'); document.body.style.overflow = 'hidden'; var d = $('.cart-drawer'); if (d) { d.removeAttribute('inert'); d.setAttribute('aria-hidden', 'false'); } },
    close: function () { document.body.classList.remove('cart-drawer-open'); document.body.style.overflow = ''; var d = $('.cart-drawer'); if (d) { d.setAttribute('inert', ''); d.setAttribute('aria-hidden', 'true'); } }
  };
  window.SFDrawer = Drawer;

  /* ---------------------------------------------------------------------
     Behaviors bound (delegation + per-element where needed)
     --------------------------------------------------------------------- */
  function bindAll(ctx) {
    bindCarousels(ctx);
    bindAccordions(ctx);
    bindDisclosures(ctx);
  }

  // ---- Global click delegation ----
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-cart-open]');
    if (t) { e.preventDefault(); if (SF.cartType === 'drawer') { e.preventDefault(); Drawer.open(); } else { window.location.href = routes.cart; } return; }

    if (e.target.closest('[data-cart-close]') || e.target.closest('.drawer-overlay')) { Drawer.close(); return; }

    // Mobile nav toggle
    var burger = e.target.closest('[data-nav-toggle]');
    if (burger) {
      var menu = $('#HeaderMobileMenu');
      if (menu) menu.toggleAttribute('hidden');
      var expanded = burger.getAttribute('aria-expanded') === 'true';
      burger.setAttribute('aria-expanded', String(!expanded));
      return;
    }

    // Footer column accordion (mobile)
    var fhead = e.target.closest('.footer__col-toggle');
    if (fhead && window.matchMedia('(max-width: 760px)').matches) {
      fhead.closest('.footer__col').classList.toggle('is-open');
      return;
    }

    // Quantity steppers
    var step = e.target.closest('[data-qty-change]');
    if (step) {
      e.preventDefault();
      var wrap = step.closest('[data-qty-wrap]');
      var input = wrap ? wrap.querySelector('[data-qty-input]') : null;
      if (input) {
        var val = parseInt(input.value, 10) || 1;
        val += parseInt(step.getAttribute('data-qty-change'), 10);
        if (val < (parseInt(input.min, 10) || 1)) val = parseInt(input.min, 10) || 1;
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // Add to cart (data-add-to-cart with data-variant-id)
    var addBtn = e.target.closest('[data-add-to-cart]');
    if (addBtn && addBtn.getAttribute('data-variant-id')) {
      e.preventDefault();
      var items = buildBundleItems(addBtn);
      var original = addBtn.innerHTML;
      addBtn.setAttribute('disabled', 'disabled');
      addBtn.innerHTML = SF.strings ? SF.strings.adding : 'Adding…';
      Cart.add(items).then(function () {
        addBtn.removeAttribute('disabled');
        addBtn.innerHTML = original;
      }).catch(function () { addBtn.removeAttribute('disabled'); addBtn.innerHTML = original; });
      return;
    }

    // Buy it now — add current selection then go straight to checkout
    var buyNow = e.target.closest('[data-buy-now]');
    if (buyNow && buyNow.getAttribute('data-variant-id')) {
      e.preventDefault();
      var bnItems = buildBundleItems(buyNow);
      buyNow.setAttribute('disabled', 'disabled');
      Cart.add(bnItems, { openDrawer: false }).then(function () { window.location.href = '/checkout'; })
        .catch(function () { buyNow.removeAttribute('disabled'); });
      return;
    }

    // Cart line stepper (+/−): relative, optimistic, debounced, keyed by stable line key.
    var stepBtn = e.target.closest('[data-line-step]');
    if (stepBtn) {
      e.preventDefault();
      var sLine = stepBtn.closest('.cart-line');
      if (!sLine) return;
      var sKey = sLine.getAttribute('data-line-key');
      var sInput = sLine.querySelector('[data-line-input]');
      var delta = parseInt(stepBtn.getAttribute('data-line-step'), 10) || 0;
      var cur = sInput ? (parseInt(sInput.value, 10) || 0) : 0;
      var next = Math.max(0, cur + delta);
      if (sInput) sInput.value = next;          // instant feedback; request is debounced
      // No is-updating here: the line must stay clickable so rapid taps keep accumulating.
      scheduleLineChange(sKey, next);
      return;
    }

    // Cart line remove (absolute qty, e.g. 0): immediate, keyed by line key.
    var lineBtn = e.target.closest('[data-line-change]');
    if (lineBtn) {
      e.preventDefault();
      var lineEl = lineBtn.closest('.cart-line');
      var key = lineEl ? lineEl.getAttribute('data-line-key') : null;
      var newQty = parseInt(lineBtn.getAttribute('data-line-change'), 10);
      if (lineEl) {
        var inpEl = lineEl.querySelector('[data-line-input]');
        if (inpEl && newQty > 0) inpEl.value = newQty;
        lineEl.classList.add('is-updating');
      }
      Cart.changeLine(key != null ? key : parseInt(lineBtn.getAttribute('data-line'), 10), newQty);
      return;
    }

    // Review video — click play → load the real player into the media box
    var playBtn = e.target.closest('[data-review-play]');
    if (playBtn) {
      e.preventDefault();
      var vurl = playBtn.getAttribute('data-video-url');
      var vmedia = playBtn.closest('.media');
      if (vurl && vmedia) vmedia.innerHTML = embedVideo(vurl);
      return;
    }

    // Cookie banner — re-open from the footer "Manage preferences" link (lets shoppers change their choice later)
    var reopenBtn = e.target.closest('[data-cookie-reopen]');
    if (reopenBtn) {
      e.preventDefault();
      var rb = document.getElementById('CookieBanner');
      if (rb) {
        rb.style.display = '';
        var rmain = rb.querySelector('[data-cookie-main]'); if (rmain) rmain.hidden = true;
        var rpanel = rb.querySelector('[data-cookie-panel]'); if (rpanel) rpanel.hidden = false;
        try {
          var cur = (window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.currentVisitorConsent && Shopify.customerPrivacy.currentVisitorConsent()) || {};
          var tg = rb.querySelectorAll('[data-consent]');
          for (var k = 0; k < tg.length; k++) {
            var c2 = tg[k].getAttribute('data-consent');
            var val = c2 === 'marketing' ? cur.marketing : cur.analytics;
            tg[k].checked = (val === 'yes' || val === true);
          }
        } catch (err) {}
      }
      return;
    }
    // Cookie banner — open the granular preferences panel
    var prefsBtn = e.target.closest('[data-cookie-prefs]');
    if (prefsBtn) {
      var bp = document.getElementById('CookieBanner');
      if (bp) {
        var main = bp.querySelector('[data-cookie-main]');
        var panel = bp.querySelector('[data-cookie-panel]');
        if (main) main.hidden = true;
        if (panel) panel.hidden = false;
      }
      return;
    }
    // Cookie banner — apply consent (accept all / reject all / save preferences) via the Customer Privacy API
    var cookieBtn = e.target.closest('[data-cookie-dismiss]');
    var saveBtn = e.target.closest('[data-cookie-save]');
    if (cookieBtn || saveBtn) {
      var consent;
      if (saveBtn) {
        consent = { analytics: false, marketing: false, preferences: false, sale_of_data: false };
        var bn = document.getElementById('CookieBanner');
        if (bn) {
          var toggles = bn.querySelectorAll('[data-consent]');
          for (var i = 0; i < toggles.length; i++) {
            var cat = toggles[i].getAttribute('data-consent');
            var on = !!toggles[i].checked;
            if (cat === 'analytics') { consent.analytics = on; consent.preferences = on; }
            if (cat === 'marketing') { consent.marketing = on; consent.sale_of_data = on; }
          }
        }
        try { localStorage.setItem('sf_cookie_choice', 'custom'); } catch (err) {}
      } else {
        var grant = cookieBtn.getAttribute('data-cookie-dismiss') === 'accepted';
        consent = { analytics: grant, marketing: grant, preferences: grant, sale_of_data: grant };
        try { localStorage.setItem('sf_cookie_choice', grant ? 'accepted' : 'declined'); } catch (err) {}
      }
      try {
        if (window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.setTrackingConsent) {
          Shopify.customerPrivacy.setTrackingConsent(consent, function () {});
        }
      } catch (err) {}
      var banner = $('.cookie-banner'); if (banner) banner.style.display = 'none';
      return;
    }
  });

  // ---- Cart line qty input change ----
  document.addEventListener('change', function (e) {
    var lineInput = e.target.closest('[data-line-input]');
    if (lineInput) {
      var lineWrap = lineInput.closest('.cart-line');
      var key = lineWrap ? lineWrap.getAttribute('data-line-key') : null;
      if (lineWrap) lineWrap.classList.add('is-updating');
      var q = parseInt(lineInput.value, 10);
      if (isNaN(q) || q < 0) q = 0;
      Cart.changeLine(key != null ? key : parseInt(lineInput.getAttribute('data-line'), 10), q);
    }
  });

  /* ---------------------------------------------------------------------
     Carousels (prev / next scroll)
     --------------------------------------------------------------------- */
  function bindCarousels(ctx) {
    $all('[data-carousel]', ctx).forEach(function (root) {
      if (root.__sfBound) return; root.__sfBound = true;
      var track = root.querySelector('[data-carousel-track]') || root;
      var navs = $all('[data-carousel-prev], [data-carousel-next]', root);
      $all('[data-carousel-prev]', root).forEach(function (b) { b.addEventListener('click', function () { scrollByCard(track, -1); }); });
      $all('[data-carousel-next]', root).forEach(function (b) { b.addEventListener('click', function () { scrollByCard(track, 1); }); });
      // Hide the arrows when everything already fits (no overflow to scroll).
      // Geometry reads batched into rAF so init/resize never force a synchronous reflow.
      var syncQueued = false;
      function syncNav() {
        if (syncQueued) return; syncQueued = true;
        requestAnimationFrame(function () {
          syncQueued = false;
          var overflow = track.scrollWidth > track.clientWidth + 2;
          navs.forEach(function (n) { n.style.display = overflow ? '' : 'none'; });
        });
      }
      syncNav();
      window.addEventListener('resize', syncNav);
      if (window.ResizeObserver) { try { new ResizeObserver(syncNav).observe(track); } catch (e) {} }
    });
  }
  function scrollByCard(track, dir) {
    var first = track.querySelector(':scope > *');
    var amount = first ? (first.getBoundingClientRect().width + 20) : track.clientWidth * 0.8;
    track.scrollBy({ left: amount * dir, behavior: 'smooth' });
  }

  /* ---------------------------------------------------------------------
     Accordions (animated, accessible)
     --------------------------------------------------------------------- */
  function bindAccordions(ctx) {
    $all('[data-accordion-trigger]', ctx).forEach(function (btn) {
      if (btn.__sfBound) return; btn.__sfBound = true;
      btn.addEventListener('click', function () {
        var item = btn.closest('.accordion__item');
        var panel = item.querySelector('.accordion__panel');
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
        if (panel) panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '0px';
      });
    });
  }

  /* ---------------------------------------------------------------------
     Disclosures (language / country selectors)
     --------------------------------------------------------------------- */
  function bindDisclosures(ctx) {
    $all('[data-disclosure]', ctx).forEach(function (root) {
      if (root.__sfBound) return; root.__sfBound = true;
      var btn = root.querySelector('[data-disclosure-button]');
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var willOpen = !root.classList.contains('is-open');
        $all('[data-disclosure].is-open').forEach(function (o) { o.classList.remove('is-open'); });
        root.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', String(willOpen));
      });
    });
  }
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-disclosure]')) {
      $all('[data-disclosure].is-open').forEach(function (o) { o.classList.remove('is-open'); });
    }
  });
  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape') {
      $all('[data-disclosure].is-open').forEach(function (o) { o.classList.remove('is-open'); });
      if (document.body.classList.contains('cart-drawer-open')) Drawer.close();
    }
  });

  /* ---------------------------------------------------------------------
     Welcome offer — capture email (popup + footer) → subscribe to Klaviyo,
     flag the cart with `welcome_discount` so the discount Function takes
     20% off in-cart, then reveal the code. Consent-aware.
     --------------------------------------------------------------------- */
  var KLAVIYO_COMPANY = 'T9GGMQ';
  var KLAVIYO_LIST = 'WZCJVG';
  var PROMO_CODE = 'WELCOME10';

  function promoClaimed() { try { return localStorage.getItem('sf_promo_claimed') === '1'; } catch (e) { return false; } }

  // Storefront language (2-letter) — tags the Klaviyo profile so emails render in the right language.
  function sfLocale() {
    try {
      var l = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en';
      return String(l).toLowerCase().slice(0, 2);
    } catch (e) { return 'en'; }
  }

  function klaviyoSubscribe(email) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Welcome 10% form',
            profile: { data: { type: 'profile', attributes: { email: email, properties: { lang: sfLocale() } } } }
          },
          relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } }
        }
      })
    });
  }

  function SFClaimPromo(email) {
    klaviyoSubscribe(email).catch(function () {});
    try {
      if (window.klaviyo && window.klaviyo.push) {
        window.klaviyo.push(['identify', { email: email, lang: sfLocale() }]);
        window.klaviyo.push(['track', 'Claimed Welcome Offer', { Code: PROMO_CODE }]);
      }
    } catch (e) {}
    // Feed the email to the tracking pixel for first-party advanced matching.
    // It runs through the Web Pixel, so it's consent-gated (only forwarded if the shopper allowed marketing).
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        var sfSeg = null; try { sfSeg = localStorage.getItem('sf_segment'); } catch (e) {}
        Shopify.analytics.publish('sf_lead', { email: email, lang: sfLocale(), segment: sfSeg || undefined });
      }
    } catch (e) {}
    try { localStorage.setItem('sf_promo_claimed', '1'); } catch (e) {}
    // Flag the cart so the discount Function applies -20% (shows live in cart).
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { welcome_discount: PROMO_CODE } })
    }).then(function () { if (window.SFCart && SFCart.refresh) SFCart.refresh(); });
  }
  window.SFClaimPromo = SFClaimPromo;

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-promo-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('input[type="email"]');
    var email = input ? (input.value || '').trim() : '';
    if (!email) return;
    form.classList.add('is-loading');
    var wrap = form.closest('[data-promo]') || form.parentNode;
    SFClaimPromo(email).then(function () {
      var res = wrap ? wrap.querySelector('[data-promo-result]') : null;
      if (res) res.hidden = false;
      form.hidden = true;
    });
  });

  function closePromoPopup() {
    var pop = document.getElementById('PromoPopup');
    if (pop) { pop.classList.remove('is-open'); document.body.classList.remove('promo-open'); }
  }
  document.addEventListener('click', function (e) { if (e.target.closest('[data-promo-close]')) closePromoPopup(); });

  function maybeShowPromoPopup() {
    var pop = document.getElementById('PromoPopup');
    if (!pop) return;
    try {
      if (localStorage.getItem('sf_promo_seen') === '1' || promoClaimed()) return;
      if (localStorage.getItem('sf_cookie_choice') === 'declined') return;
    } catch (e) {}
    setTimeout(function () {
      pop.classList.add('is-open');
      document.body.classList.add('promo-open');
      try { localStorage.setItem('sf_promo_seen', '1'); } catch (e) {}
    }, 6000);
  }

  /* ---------------------------------------------------------------------
     Init
     --------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () { bindAll(document); syncGifts(); maybeShowPromoPopup(); });
  // Re-bind after Shopify section editor re-renders a section
  document.addEventListener('shopify:section:load', function (e) { bindAll(e.target); });
})();
